import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { TrueFalseItem } from '../../services/mockQuizService';
import type { QuizProgress } from '../QuizBlock';

interface TrueFalsePreviewProps {
  items: TrueFalseItem[];
  initialAnswers?: Record<number, { selected: 'True' | 'False'; correct: boolean }>;
  onAnswersChange?: (answers: Record<number, { selected: 'True' | 'False'; correct: boolean }>) => void;
  onProgressChange?: (progress: QuizProgress) => void;
}

export const TrueFalsePreview = ({ items, initialAnswers, onAnswersChange, onProgressChange }: TrueFalsePreviewProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, { selected: 'True' | 'False'; correct: boolean }>>(initialAnswers || {});

  const currentItem = items[currentIndex];
  const currentAnswer = answers[currentIndex];
  const showResult = currentAnswer !== undefined;
  const isCorrect = currentAnswer?.correct;

  // Calculate and report progress
  const updateProgress = useCallback(() => {
    const completed = Object.keys(answers).length;
    const correct = Object.values(answers).filter(a => a.correct).length;
    onProgressChange?.({ total: items.length, completed, correct });
  }, [answers, items.length, onProgressChange]);

  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  const handleSubmit = (answer: 'True' | 'False') => {
    const correct = answer === currentItem.answer;
    const newAnswers = {
      ...answers,
      [currentIndex]: { selected: answer, correct }
    };
    setAnswers(newAnswers);
    onAnswersChange?.(newAnswers);
  };

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  if (!currentItem) return null;

  return (
    <div className="py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-400">
          Statement {currentIndex + 1} of {items.length}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-600 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex === items.length - 1}
            className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-600 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Statement */}
      <div className="bg-slate-50 rounded-lg p-4 mb-4">
        <p className="text-sm text-slate-700">{currentItem.statement}</p>
      </div>

      {/* Buttons */}
      {!showResult ? (
        <div className="flex gap-3">
          <button
            onClick={() => handleSubmit('True')}
            className="flex-1 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-colors"
          >
            True
          </button>
          <button
            onClick={() => handleSubmit('False')}
            className="flex-1 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-colors"
          >
            False
          </button>
        </div>
      ) : (
        <div className={`p-3 rounded-lg text-sm ${isCorrect ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
          <p className="font-medium mb-1">
            {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
          </p>
          <p className="text-slate-600 mb-2">
            The answer is <span className="font-medium">{currentItem.answer}</span>
          </p>
          {currentItem.explanation && (
            <p className="text-slate-500 text-xs">{currentItem.explanation}</p>
          )}
        </div>
      )}

      {/* Progress dots */}
      <div className="flex justify-center gap-1 mt-4">
        {items.map((_, idx) => {
          const answered = answers[idx] !== undefined;
          const wasCorrect = answers[idx]?.correct;
          return (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentIndex
                  ? 'bg-slate-600 ring-2 ring-slate-300'
                  : answered
                  ? wasCorrect ? 'bg-emerald-400' : 'bg-red-400'
                  : 'bg-slate-200 hover:bg-slate-300'
              }`}
            />
          );
        })}
      </div>
    </div>
  );
};
